/**
 * @file api.ts
 * @brief TanStack Query hooks for home-screen banner management.
 *
 * @details
 * Wraps the existing `/api/banners/*` admin endpoints that the mobile
 * Super Admin tab already uses. Image uploads are sent as base-64 data
 * URLs, matching the mobile `BannerManagementModal` contract.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/{client,endpoints}
 *
 * @references
 * - BACKEND/src/features/banners/routes.js
 * - FRONTEND/src/screens/SuperAdmin/components/BannerManagementModal.tsx
 *
 * @author DES
 * @date 07-06-2026
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
} from "@/shared/api/client";
import { bannerEndpoints } from "@/shared/api/endpoints";
import {
  BannerSchema,
  BannersListSchema,
  type Banner,
  type BannerInput,
} from "./schemas";

export const bannerKeys = {
  all: ["admin", "banners"] as const,
  list: () => [...bannerKeys.all, "list"] as const,
};

export function useBanners() {
  return useQuery<Banner[]>({
    queryKey: bannerKeys.list(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(bannerEndpoints.list);
      return BannersListSchema.parse(raw);
    },
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

export function useUploadBannerImage() {
  return useMutation<{ imageUrl: string }, Error, File>({
    mutationFn: async (file) => {
      const dataUrl = await fileToBase64(file);
      const res = await apiPost<{ imageUrl: string }>(bannerEndpoints.upload, {
        imageData: dataUrl,
        contentType: file.type || "image/jpeg",
      });
      return { imageUrl: res.imageUrl };
    },
  });
}

export function useCreateBanner() {
  const qc = useQueryClient();

  return useMutation<Banner, Error, BannerInput>({
    mutationFn: async (input) => {
      const raw = await apiPost<unknown>(bannerEndpoints.create, input);
      return BannerSchema.parse(raw);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: bannerKeys.list() });
    },
  });
}

export function useUpdateBanner() {
  const qc = useQueryClient();

  return useMutation<Banner, Error, { id: string; input: Partial<BannerInput> }>({
    mutationFn: async ({ id, input }) => {
      const raw = await apiPatch<unknown>(bannerEndpoints.byId(id), input);
      return BannerSchema.parse(raw);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: bannerKeys.list() });
    },
  });
}

export function useDeleteBanner() {
  const qc = useQueryClient();

  return useMutation<{ id: string }, Error, string>({
    mutationFn: async (id) =>
      apiDelete<{ id: string }>(bannerEndpoints.byId(id)),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: bannerKeys.list() });
    },
  });
}
